import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  TablePagination,
  TableSortLabel,
  Rating,
  Tooltip,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  VisibilityOutlined,
  ReceiptLongOutlined,
  ArrowDropUp,
  ArrowDropDown,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";

import { ORDERS_BY_TOY_USER_UUID } from "./graphQL/order_qry";
import { USER_DATA_BY_UUID } from "./graphQL/user_qry";

import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";

import placeholderImage from "./assets/r3play-logo-sml.png";



import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userUuid }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`/user/${userUuid}/profile`}
      style={{
        color: "red",
        textDecoration: "none",
        fontSize: "14px",
      }}
      onMouseEnter={(e) => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const MyRatings = () => {
  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(833); // Use 833px breakpoint for mobile size
  const [mobileSortBy, setMobileSortBy] = useState("purchasedDate");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("purchasedDate");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [ratings, setRatings] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [userRatingNo, setUserRatingNo] = useState(0); // Overall user rating
  const [usrDataObj, setUsrDataObj] = useState({});
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);  

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  const [getRatingsData] = useManualQuery(
    ORDERS_BY_TOY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  const [getUsrData] = useManualQuery(USER_DATA_BY_UUID);

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the sort panel container
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {      
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);



  const setUserData = async () => {
    if (!userUuid) return;
    const {
      loading: loadingUsrData,
      error: errorCurrUsrData,
      data: usrData,
    } = await getUsrData({
      variables: {
        uuid: userUuid,
      },
    });
    if (!loadingUsrData && usrData) {
      setUsrDataObj(usrData.userDataByUuid);      
      setUserRatingNo(Number(usrData.userDataByUuid.ratingNo) || 0)
    } else {
      console.log("errorCurrUsrData: ", errorCurrUsrData);
    }
  };

 useEffect(() => {
    (async () => setUserData())();
  }, [userUuid]);



  
  const setRatingList = async () => {    
    if (!userUuid) return;
    const {
      loading: loadingRatingListData,
      error: errorRatingListData,
      data: ratingListData,
    } = await getRatingsData({
      variables: {
        userUuid: userUuid,
      },
    });
    
    if (!loadingRatingListData && ratingListData && ratingListData.ordersByToyUseruuid) {              
      setRatings(
        ratingListData.ordersByToyUseruuid.map(order => ({
          id: order.orderId,
          uuid: order.uuid,
          photo: order.toyId.toyImages[0]?.fileName || placeholderImage,
          title: order.toyId.title,
          ratingNo: order.toyId.ratingNo,
          ratingComment: order.toyId.ratingComment,
          purchasedBy: order.userId.username,
          purchasedByUuid: order.userId.userUuid,
          purchasedDate: order.orderDate ? dayjs(order.orderDate).format("MMM D, YYYY") : ""

        }))        
      ); 
      setIsLoaded(true);         
    } else {
      console.log("errorRatingListData: ", errorRatingListData);           
    };
  };

  useEffect(() => {
    (async () => setRatingList())();
  }, [userUuid]);




  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Sort data
  const getSortedData = () => {
    let sortedData = [...ratings];

    if (orderBy) {
      sortedData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        // Handle title sorting (case insensitive)
        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle date sorting for purchasedDate
        if (orderBy === "purchasedDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        // Handle purchasedBy sorting (case insensitive)
        if (orderBy === "purchasedBy") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle rating number sorting
        if (orderBy === "ratingNo") {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  };

  const sortedData = getSortedData();

  // Get current page data from sorted results
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet>
        <title>R3PLAY - My Ratings</title>
        <meta
          name="description"
          content="View and manage ratings for toys you've sold on R3PLAY"
        />
        <meta
          property="keywords"
          content="Toy ratings, buyer feedback, seller ratings, toy reviews"
        />
      </Helmet>
      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "16px",
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#333",
              mb: 1,
            }}
          >
            My Ratings
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#666",
              mb: 2,
            }}
          >
            View ratings and feedback from buyers
          </Typography>

          {/* Mobile Overall Rating */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              My Overall Rating
            </Typography>
            <StyledTooltip title={`${userRatingNo} of 5`}>
              <Box sx={{ display: "inline-block" }}>
                <Rating
                  value={userRatingNo}
                  precision={0.5}
                  readOnly
                  size="medium"
                  sx={{
                    "& .MuiRating-iconFilled": {
                      color: "#E53935",
                    },
                    "& .MuiRating-iconEmpty": {
                      color: "#e0e0e0",
                    },
                  }}
                />
              </Box>
            </StyledTooltip>
          </Box>

          {/* Number of Ratings */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Total No of Ratings: {ratings.length}
            </Typography>
          </Box>
        </Box>

        {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
          ) 
          : isLoaded && ratings.length === 0 ? 
          (
            <EmptyState 
              type="ratings"
              showIcon={false}
              title="No Ratings Available"
              description="You haven't received any ratings yet."
            />
          ) : (
            <>
        
        {/* Mobile Sort Panel */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              width: "100%",
            }}
          >
            <Box sx={{ width: "230px", position: "relative" }} data-sort-panel>
              <TextField
                label="Sort by"
                value={
                  mobileSortBy
                    ? (() => {
                        const options = {
                          title: "Title",
                          ratingNo: "Rating",
                          purchasedBy: "Purchased By",
                          purchasedDate: "Purchased Date",
                        };
                        return options[mobileSortBy] || "";
                      })()
                    : ""
                }
                onClick={() => setSortPanelOpen(!sortPanelOpen)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {mobileSortBy &&
                        (order === "desc" ? (
                          <ArrowDownward sx={{ fontSize: 20, color: "#666" }} />
                        ) : (
                          <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                        ))}
                      {sortPanelOpen ? (
                        <ArrowDropUp sx={{ color: "#666" }} />
                      ) : (
                        <ArrowDropDown sx={{ color: "#666" }} />
                      )}
                    </Box>
                  ),
                }}
                sx={{
                  width: "230px",
                  cursor: "pointer",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    paddingRight: "8px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    pr: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    cursor: "pointer",
                  },
                }}
              />

              {/* Sort options panel */}
              {sortPanelOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "40px",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderTop: "none",
                    borderRadius: "0 0 4px 4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 1000,
                  }}
                >
                  <RadioGroup
                    value={mobileSortBy}
                    onChange={(e) => {
                      setMobileSortBy(e.target.value);
                      setOrder("desc");
                      setOrderBy(e.target.value);
                      setPage(0);
                    }}
                  >
                    {[
                      { value: "title", label: "Title" },
                      { value: "ratingNo", label: "Rating" },
                      { value: "purchasedBy", label: "Purchased By" },
                      { value: "purchasedDate", label: "Purchased Date" },
                    ].map((option) => (
                      <Box
                        key={option.value}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                          pl: 0,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <FormControlLabel
                          value={option.value}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: "#666",
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "14px", color: "#333" }}>
                              {option.label}
                            </Typography>
                          }
                          sx={{ margin: 0, flex: 1 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "55px",
                            justifyContent: "space-between",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileSortBy(option.value);
                              setOrder("desc");
                              setOrderBy(option.value);
                              setPage(0);
                              setSortPanelOpen(false);
                            }}
                            sx={{
                              p: 0.25,
                              backgroundColor:
                                mobileSortBy === option.value && order === "desc"
                                  ? "#fce4ec"
                                  : "transparent",
                            }}
                          >
                            <ArrowDownward
                              fontSize="small"
                              sx={{ fontSize: 20, color: "#666" }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileSortBy(option.value);
                              setOrder("asc");
                              setOrderBy(option.value);
                              setPage(0);
                              setSortPanelOpen(false);
                            }}
                            sx={{
                              p: 0.25,
                              backgroundColor:
                                mobileSortBy === option.value && order === "asc"
                                  ? "#fce4ec"
                                  : "transparent",
                            }}
                          >
                            <ArrowUpward
                              fontSize="small"
                              sx={{ fontSize: 20, color: "#666" }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </RadioGroup>
                </Box>
              )}
            </Box>
          </Box>

          {/* Mobile Cards */}
          {paginatedData.map((rating, index) => (
            <Box
              key={rating.id}
              sx={{
                backgroundColor: index % 2 === 0 ? "#FDECF0" : "white",
                borderRadius: "8px",
                mb: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableBody>
                  {/* Row 1: Image + Title */}
                  <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell sx={{ py: 2, width: "135px", border: "none" }}>
                      <Avatar
                        src={rating.photo}
                        alt={rating.title}
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "8px",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                          lineHeight: 1.4,
                        }}
                      >
                        {rating.title}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Row 2: Rating + Feedback */}
                  <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Buyer's Rating
                      </Typography>
                      <StyledTooltip title={`${rating.ratingNo} of 5`}>
                        <Box sx={{ display: "inline-block" }}>
                          <Rating
                            value={rating.ratingNo}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{
                              "& .MuiRating-iconFilled": {
                                color: "#E53935",
                              },
                            }}
                          />
                        </Box>
                      </StyledTooltip>
                    </TableCell>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Buyer's Feedback
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {rating.ratingComment}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Row 3: Purchased By + Date */}
                  <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Purchased By
                      </Typography>
                      <UserLink 
                        username = {rating.purchasedBy}
                        userUuid={rating.purchasedByUuid} />
                    </TableCell>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Purchased Date
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {rating.purchasedDate}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Row 4: Actions */}
                  <TableRow>
                    <TableCell colSpan={2} sx={{ pt: 2, pb: 0, border: "none" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-start",
                        }}
                      >
                        <StyledTooltip title="View Order Detail">
                          <IconButton
                            size="small"
                            onClick={() => alert('Under Development')}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <ReceiptLongOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                        <StyledTooltip title="View Toy Detail">
                          <IconButton
                            size="small"
                            onClick={() => alert('Under Development')}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Card bottom padding */}
              <Box sx={{ pb: 2 }} />
            </Box>
          ))}

          {/* Mobile Pagination */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 15, 20]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 1,
                  paddingRight: 1,
                  minHeight: "52px",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "14px",
                  color: "#666",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "14px",
                },
                "& .MuiTablePagination-actions": {
                  "& .MuiIconButton-root": {
                    padding: "6px",
                  },
                },
              }}
            />
          </Box>
          </>
          )}
        <FloatingMenu left={floatMenuLeftMargin} />
        
      </Box>
      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "91px",
            p: 1,
            pl: 2,
            pr: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "100%",
            }}
          >
            {/* Title Section */}
            <Box>
              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                My Ratings
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                View ratings and feedback from buyers
              </Typography>
            </Box>

            {/* Overall Rating Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  My Overall Rating
                </Typography>
                <StyledTooltip title={`${userRatingNo} of 5`}>
                  <Box sx={{ display: "inline-block" }}>
                    <Rating
                      value={userRatingNo}
                      precision={0.1}
                      readOnly
                      size="large"
                      sx={{
                        "& .MuiRating-iconFilled": {
                          color: "#E53935",
                        },
                        "& .MuiRating-iconEmpty": {
                          color: "#e0e0e0",
                        },
                      }}
                    />
                  </Box>
                </StyledTooltip>
              </Box>

              {/* Number of Ratings */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  Total No of Ratings: {ratings.length}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

          {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
          ) 
          : isLoaded && ratings.length === 0 ? 
          (
            <EmptyState 
              type="ratings"
              showIcon={false}
              title="No Ratings Available"
              description="You haven't received any ratings yet."
            />
          ) : (
            <>
          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "white" }}>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      maxWidth: "80px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    Photo
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={orderBy === "title" ? order : "asc"}
                      onClick={() => handleRequestSort("title")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Title/Summary
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "160px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "ratingNo"}
                      direction={orderBy === "ratingNo" ? order : "asc"}
                      onClick={() => handleRequestSort("ratingNo")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Buyer's Rating
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "175px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    Buyer's Feedback
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "130px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "purchasedBy"}
                      direction={orderBy === "purchasedBy" ? order : "asc"}
                      onClick={() => handleRequestSort("purchasedBy")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Purchased By
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      width: "170px",
                      color: "#333",
                      py: 2,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "purchasedDate"}
                      direction={orderBy === "purchasedDate" ? order : "asc"}
                      onClick={() => handleRequestSort("purchasedDate")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Purchased Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      py: 2,
                      minWidth: "100px",
                      textAlign: "center",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((rating) => (
                  <React.Fragment key={rating.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                        ...(isTablet && {
                          "& td": {
                            borderBottom: "none",
                          },
                        }),
                      }}
                    >
                      <TableCell sx={{ py: 2, pb: isTablet ? 0 : 2 }}>
                        <Avatar
                          src={rating.photo}
                          alt={rating.title}
                          sx={{
                            width: isTablet ? 40 : 80,
                            height: isTablet ? 40 : 80,
                            borderRadius: "8px",
                          }}
                        />
                      </TableCell>

                      {/* Title Cell - Spans 6 columns on tablet, normal on desktop */}
                      <TableCell
                        colSpan={isTablet ? 6 : 1}
                        sx={{
                          py: isTablet ? 1 : 2,
                          maxWidth: "300px",
                          "@media (max-width: 1360px)": {
                            maxWidth: "none",
                            paddingBottom: 0,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "500",
                            lineHeight: 1.4,
                          }}
                        >
                          {rating.title}
                        </Typography>
                      </TableCell>

                      {/* Desktop columns - hidden on tablet */}
                      {!isTablet && (
                        <>
                          <TableCell sx={{ py: 2 }}>
                            <StyledTooltip title={`${rating.ratingNo} of 5`}>
                              <Box sx={{ display: "inline-block" }}>
                                <Rating
                                  value={rating.ratingNo}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  sx={{
                                    "& .MuiRating-iconFilled": {
                                      color: "#E53935",
                                    },
                                  }}
                                />
                              </Box>
                            </StyledTooltip>
                          </TableCell>
                          <TableCell sx={{ py: 2, maxWidth: "200px" }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                lineHeight: 1.4,
                              }}
                            >
                              {rating.ratingComment}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <UserLink 
                                username = {rating.purchasedBy}
                                userUuid={rating.purchasedByUuid} />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {rating.purchasedDate}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              <StyledTooltip title="View Order Detail">
                                <IconButton
                                  size="small"
                                  onClick={() => alert('Under Development')}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <ReceiptLongOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="View Toy Detail">
                                <IconButton
                                  size="small"
                                  onClick={() => alert('Under Development')}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* Second row for tablet view */}
                    {isTablet && (
                      <TableRow
                        key={`${rating.id}-details`}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f9f9f9",
                          },
                          "& td": {
                            borderTop: "none",
                          },
                        }}
                      >
                        {/* Empty cell for Photo and Title column */}
                        <TableCell sx={{ py: 0 }}></TableCell>
                        <TableCell sx={{ py: 0 }}></TableCell>

                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <StyledTooltip title={`${rating.ratingNo} of 5`}>
                            <Box sx={{ display: "inline-block" }}>
                              <Rating
                                value={rating.ratingNo}
                                precision={0.5}
                                readOnly
                                size="small"
                                sx={{
                                  "& .MuiRating-iconFilled": {
                                    color: "#E53935",
                                  },
                                }}
                              />
                            </Box>
                          </StyledTooltip>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1, maxWidth: "200px" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                              lineHeight: 1.4,
                            }}
                          >
                            {rating.ratingComment}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <UserLink 
                              username = {rating.purchasedBy}
                              userUuid={rating.purchasedByUuid} />
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {rating.purchasedDate}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            <StyledTooltip title="View Order Detail">
                              <IconButton
                                size="small"
                                onClick={() => alert('Under Development')}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <ReceiptLongOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="View Toy Detail">
                              <IconButton
                                size="small"
                                onClick={() => alert('Under Development')}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 15, 20]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid #e0e0e0",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "14px",
                  color: "#666",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "14px",
                },
              }}
            />
          </TableContainer>
        </>
      )}
        <FloatingMenu left={floatMenuLeftMargin} />
      </Box>
    </>
  );
};

export default MyRatings;
